import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BellRing, RefreshCw } from 'lucide-react';
import { SERVICE_REQUEST_TRANSITIONS, canTransition } from '@/lib/domain';
import { ServiceRequestStatus, ServiceRequestType, VENUE_EVENTS } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime } from '@/Hooks/useRealtime';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import {
  SERVICE_CALL_ACTIONS,
  ServiceCallDrawer,
  requestLabel,
  type ServiceCallAction,
  type ServiceRequestRow,
} from './ServiceCallDrawer';
import {
  apiMessage,
  formatDuration,
  useBranchRooms,
  useBranches,
  useNow,
  useQueryParams,
} from './shared';

const STATUSES = Object.values(ServiceRequestStatus);
const TYPES = Object.values(ServiceRequestType);

/** The two statuses that are still somebody's problem. */
const OPEN_STATUSES = `${ServiceRequestStatus.CREATED},${ServiceRequestStatus.ESCALATED},${ServiceRequestStatus.ACCEPTED}`;

/**
 * §18 — the service-request queue, from the manager's side.
 *
 * The waiter app owns the working end of this: accepting a call, resolving it,
 * escalating what is going stale. What had no interface anywhere was the
 * *supervisory* view — which requests are ageing, which have been escalated to a
 * head waiter, and which nobody has picked up. That is exactly what escalation
 * is for, and it was landing on a screen that did not exist.
 *
 * The ageing clock is the point of the screen, so it ticks locally rather than
 * refetching, and `SERVICE_REQUEST_UPDATED` re-reads the queue. The event is
 * emitted to the request's **branch** room (plus its area, plus the assignee's
 * private user room), so the rooms joined below are the branches the visible
 * queue is actually about — a manager is in none of them until asked.
 *
 * D-08 — every action here is gated on `SERVICE_REQUEST_TRANSITIONS`, the same
 * machine the service asserts against, rather than on a hand-rolled "is it
 * still open". The screen used to offer **Resolve** on a `CREATED` row — the
 * unaccepted call a supervisor opens this queue to clear — and the server
 * answered `400 Illegal service request transition: CREATED → DONE`, because
 * DONE is only reachable once somebody has accepted. Accept itself existed on
 * the API and on no screen, so the queue had no way out at all. The table of
 * actions now lives in `ServiceCallDrawer.tsx` and both surfaces read it.
 *
 * Frame `64:3024` opens one call in a drawer beside the queue, which is here.
 * What it also does is strip the rows down to an id, a table and a type — no
 * status, no assignee, no actions — and a supervisor cannot triage four calls
 * by opening four drawers, so the columns stay.
 */
export default function ServiceRequests() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const now = useNow(1000);

  const [status, setStatus] = useState(OPEN_STATUSES);
  const [type, setType] = useState('');
  const [branchId, setBranchId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  /**
   * §28 — one replay key per intended operation, not per click.
   *
   * `withOperationId` and the `X-Operation-Id` header make every one of these
   * four routes replay-safe, and a drawer whose primary action is one button is
   * exactly where a double press happens. Keyed on request + action and held
   * until the call succeeds, so the second press is answered from the first's
   * record instead of acting twice; a fresh id per click would carry the header
   * and none of the protection.
   */
  const operationIds = useRef(new Map<string, string>());
  const operationIdFor = (key: string): string => {
    const existing = operationIds.current.get(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    operationIds.current.set(key, id);
    return id;
  };

  const params = useQueryParams({ limit: 100, status, type, branchId });
  const { data, loading, error, reload } = useApi<unknown>('/service-requests', params);
  const requests = useMemo(() => rowsOf<ServiceRequestRow>(data).rows, [data]);

  const branchRooms = useBranchRooms(branchId, requests);

  useRealtime(VENUE_EVENTS.SERVICE_REQUEST_UPDATED, () => reload(), branchRooms);

  const openRequest = useMemo(
    () => requests.find((row) => row.id === openId) ?? null,
    [requests, openId],
  );

  const run = async (action: ServiceCallAction, row: ServiceRequestRow) => {
    const key = `${action}:${row.id}`;
    const entry = SERVICE_CALL_ACTIONS.find((candidate) => candidate.action === action);
    setBusy(key);
    try {
      await api.post(
        `/service-requests/${row.id}/${action}`,
        {},
        { headers: { 'X-Operation-Id': operationIdFor(key) } },
      );
      operationIds.current.delete(key);
      if (entry) toast.success(t(entry.success));
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  /** How long this request has been open — the number a supervisor acts on. */
  const waitingFor = (row: ServiceRequestRow): number => {
    const from = new Date(row.createdAt).getTime();
    const to = row.doneAt ? new Date(row.doneAt).getTime() : now;
    return Math.max(0, (to - from) / 1000);
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Icon as={BellRing} size="lg" />
            {t('platform.serviceQueue.title')}
          </h1>
          <p className="text-sm text-ink-muted">
            {t('platform.serviceQueue.subtitle')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
          <Icon as={RefreshCw} />
          {t('platform.approvals.refresh')}
        </Button>
      </header>

      <Card>
        <CardContent className="pt-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t('platform.serviceQueue.status')}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value={OPEN_STATUSES}>{t('platform.serviceQueue.openOnly')}</option>
                <option value="">{t('platform.serviceQueue.allStatuses')}</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`platform.serviceQueue.statusLabel.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label={t('platform.serviceQueue.type')}>
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">{t('platform.serviceQueue.allTypes')}</option>
                {TYPES.map((s) => (
                  <option key={s} value={s}>
                    {t(`platform.serviceQueue.typeLabel.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>

            {!!branches.length && (
              <Field label={t('platform.serviceQueue.branch')}>
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">{t('platform.serviceQueue.allBranches')}</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
        </CardContent>
      </Card>

      {loading && !requests.length ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('platform.serviceQueue.request')}</TH>
                <TH>{t('platform.serviceQueue.where')}</TH>
                <TH>{t('platform.serviceQueue.assignee')}</TH>
                <TH>{t('platform.serviceQueue.status')}</TH>
                <TH className="text-end">{t('platform.serviceQueue.waiting')}</TH>
                <TH className="text-end">{t('platform.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {!requests.length && (
                <TableEmpty colSpan={6}>
                  <EmptyState
                    title={t('platform.serviceQueue.empty')}
                    hint={t('platform.serviceQueue.emptyHint')}
                  />
                </TableEmpty>
              )}

              {requests.map((row) => {
                // Ageing is about the states somebody still owes an answer on.
                const open =
                  row.status === ServiceRequestStatus.CREATED ||
                  row.status === ServiceRequestStatus.ACCEPTED ||
                  row.status === ServiceRequestStatus.ESCALATED;
                const seconds = waitingFor(row);

                return (
                  <TR key={row.id}>
                    <TD>
                      <button
                        type="button"
                        className="text-start font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => setOpenId(row.id)}
                      >
                        {requestLabel(row, t)}
                      </button>
                      {row.note && (
                        <div className="text-xs text-ink-muted">{row.note}</div>
                      )}
                    </TD>
                    <TD>
                      {row.table ? t('platform.serviceQueue.tableNumber', { number: row.table.number }) : '—'}
                      {row.area && (
                        <div className="text-xs text-ink-muted">
                          {row.area.name}
                        </div>
                      )}
                    </TD>
                    <TD>
                      {row.assignee
                        ? [row.assignee.firstName, row.assignee.lastName].filter(Boolean).join(' ')
                        : t('platform.serviceQueue.unassigned')}
                    </TD>
                    <TD>
                      <Badge tone={toneForStatus(row.status)}>
                        {t(`platform.serviceQueue.statusLabel.${row.status}`)}
                      </Badge>
                    </TD>
                    <TD
                      className={
                        open && seconds > 300
                          ? 'text-end font-medium tabular-nums text-red-600 dark:text-red-400'
                          : 'text-end tabular-nums'
                      }
                    >
                      {formatDuration(seconds)}
                    </TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {/*
                          Accept is the missing link: CREATED and ESCALATED both
                          reach ACCEPTED, and ACCEPTED is the only state from
                          which the server will mark a call DONE. `accept` also
                          assigns the request to whoever pressed it.
                        */}
                        {SERVICE_CALL_ACTIONS.filter(
                          (entry) =>
                            can(entry.permission) &&
                            canTransition(SERVICE_REQUEST_TRANSITIONS, row.status, entry.to),
                        ).map((entry) => (
                          <Button
                            key={entry.action}
                            size="sm"
                            variant={entry.variant}
                            disabled={busy === `${entry.action}:${row.id}`}
                            onClick={() => void run(entry.action, row)}
                          >
                            {t(entry.label)}
                          </Button>
                        ))}

                        {/*
                          DONE and CANCELLED have no outgoing edge at all, so
                          the empty cell is a rule rather than a permission
                          problem — say which, instead of leaving a blank.
                        */}
                        {!SERVICE_REQUEST_TRANSITIONS[row.status].length && (
                          <span className="text-xs text-ink-muted">
                            {t('platform.serviceQueue.terminal')}
                          </span>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <ServiceCallDrawer
        request={openRequest}
        now={now}
        busy={busy}
        onAction={(action, row) => void run(action, row)}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}
